import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSegment, UrlMatchResult } from '@angular/router';
import { HandlerRedirectToLanguageComponent } from '@app/routing-lang/handler-redirect-to-language/handler-redirect-to-language.component';
import { HomeComponent } from '@app/home/home.component';
import { RedirectPrefixComponent } from '@app/routing-lang/redirect-prefix/redirect-prefix.component';
import { RegisterComponent } from '@app/security/register/register.component';
import { LoginComponent } from '@app/security/login/login.component';
import { RecoverPasswordComponent } from '@app/security/recover-pass/recover-password/recover-password.component';
import { RecoverPasswordFinishComponent } from '@app/security/recover-pass/recover-password-finish/recover-password-finish.component';
import { PageNotFoundComponent } from '@app/security/page-not-found/page-not-found.component';
import { PrivacyPolicyComponent } from '@app/pages/privacy-policy/privacy-policy.component';
import { CookiesPolicyComponent } from '@app/pages/cookie-policy/cookie-policy.component';
import { AvisoLegalComponent } from '@app/pages/aviso-legal/aviso-legal.component';
import { LandingCalculadoraComponent } from '@app/pages/landing-calculadora/landing-calculadora.component';
import { TermsComponent } from '@app/pages/terms/terms.component';
import { VerifyAccountComponent } from '@app/security/verify-account/verify-account.component';
import { UserRouteAccessGuard } from '@shared/guard/user-route-access.guard';
import { OpenSignUpModalGuard } from '@shared/guard/open-signup-modal.guard';
import { LoginHashComponent } from '@app/security/login-hash/login-hash.component';
import { ManageConsentsComponent } from './security/manage-consents/manage-consents.component';

const ALLOWED_LANGS = ['es', 'en']; //Idiomas utilizados 
export function langMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (!segments.length) return null;

  const first = segments[0].path;
  if (!ALLOWED_LANGS.includes(first)) return null;

  return {
    consumed: [segments[0]],
    posParams: { lang: segments[0] },
  };
}

const routesOfShell = [
  {
    path: 'registrarse',
    component: RegisterComponent,
  },
  {
    path: 'ingresar',
    component: LoginComponent,
    canActivate: [OpenSignUpModalGuard],
  },
  {
    path: 'recover-password',
    component: RecoverPasswordComponent,
  },
  {
    path: 'recover-password/:hash',
    component: RecoverPasswordFinishComponent,
  },
  {
    path: 'politica-de-privacidad',
    component: PrivacyPolicyComponent,
  },
  {
    path: 'politica-de-cookies',
    component: CookiesPolicyComponent,
  },
  {
    path: 'terminos-condiciones',
    component: TermsComponent,
  },
  {
    path: 'aviso-legal',
    component: AvisoLegalComponent,
  },
  /*
  {
    path: 'calculadora-de-rentabilidad',
    component: LandingCalculadoraComponent,
  },
  */
  {
    path: 'verify-account/:hash',
    component: VerifyAccountComponent,
  },
  {
    path: 'verify-hash-user/:hash',
    component: LoginHashComponent,
  },
  {
    path: 'manage-consents/:hash/:action',
    component: ManageConsentsComponent,
  },
  {
    path: 'vende',
    loadComponent: () =>
      import('./sell/sell-your-apartment/sell-your-apartment.component').then(
        (c) => c.SellYourApartmentComponent
      ),
  },
  {
    path: 'buildings',
    loadComponent: () =>
      import('./sell/sell-your-buildings/sell-your-buildings.component').then(
        (c) => c.SellYourBuildingsComponent
      ),
  },
  {
    path: 'estadisticas/:province',
    loadComponent: () =>
      import('./statistics/statistics.component').then(
        (c) => c.StatisticsComponent
      ),
  },
  {
    path: 'invertir',
    loadChildren: () => import('./buy/buy.module').then((m) => m.BuyModule),
  },
  {
    path: 'campania/:campaignName',
    loadChildren: () =>
      import('./campaign/campaign.module').then((m) => m.CampaignModule),
  },
  {
    path: 'sobre-nosotros',
    loadChildren: () =>
      import('./about/about.module').then((m) => m.AboutModule),
  },
 /* {
    path: 'servicios',
    loadChildren: () =>
      import('./service-inv/service-inv.module').then(
        (m) => m.ServiceInvModule
      ),
  },
  */
  {
    path: 'como-funciona',
    loadChildren: () =>
      import('./how-it-works/how-it-works.module').then(
        (m) => m.HowItWorksModule
      ),
  },
  
  {
    path: 'tablero-de-usuario',
    loadChildren: () =>
      import('./user-dashboard/user-dashboard.module').then(
        (m) => m.UserDashboardModule
      ),
    data: {
      authorities: [
        'ROLE_USER',
        'ROLE_ADMIN',
        'ROLE_AGENT',
        'ROLE_USER',
        'ROLE_PHOTOGRAPHER',
        'ROLE_TECHNICAL',
        'ROLE_ARCHITECT',
      ],
    },
    canActivate: [UserRouteAccessGuard],
  },
  {
    path: 'inversores',
    loadChildren: () =>
      import('./landing-inversores/landing-inversores.module')
        .then(m => m.LandingInversoresModule)
  },
  { path: 'page-not-found', component: PageNotFoundComponent },
  {
    path: '**',
    component: RedirectPrefixComponent,
  },
];

const routes: Routes = [
  {
    matcher: langMatcher,
    component: HandlerRedirectToLanguageComponent,
    children: [
      {
        path: '',
        component: HomeComponent,
      },
      ...routesOfShell,
    ],
  },
  // Landing de campañas: URL corta sin prefijo de idioma (/inversores en vez
  // de /es/inversores). Se mantiene tambien la ruta con prefijo dentro de
  // routesOfShell para no romper links/campañas que ya apuntan a /es/inversores.
  // Usa el mismo HandlerRedirectToLanguageComponent para que el idioma quede
  // fijado en español (no llega parametro lang => cae al idioma por defecto).
  {
    path: 'inversores',
    component: HandlerRedirectToLanguageComponent,
    loadChildren: () =>
      import('./landing-inversores/landing-inversores.module')
        .then(m => m.LandingInversoresModule),
  },
  // Cualquier ruta sin idioma (incluida la raiz "/") entra aqui: RedirectPrefixComponent
  // prefija con el idioma activo (translateService.currentLang), no siempre "/es".
  { path: '**', component: RedirectPrefixComponent },

];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      scrollPositionRestoration: 'top',
      useHash: false,
      anchorScrolling: 'enabled',
      scrollOffset: [0, 0],
      onSameUrlNavigation: 'reload'
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
